"use client";

import { useEffect, useState } from "react";
import type { Rect } from "@/lib/tour/placement";

export type AnchorPhase = "seeking" | "anchored" | "centered";

export interface AnchorState {
  phase: AnchorPhase;
  rect: Rect | null;
}

/** Give streamed/suspended sections this long to mount before falling back to a centered card. */
const SEEK_TIMEOUT_MS = 8000;

function toRect(el: Element): Rect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function rectChanged(a: Rect | null, b: Rect): boolean {
  if (!a) return true;
  return (
    Math.abs(a.top - b.top) > 0.5 ||
    Math.abs(a.left - b.left) > 0.5 ||
    Math.abs(a.width - b.width) > 0.5 ||
    Math.abs(a.height - b.height) > 0.5
  );
}

/**
 * Find `[data-tour="<anchor>"]` and report its live viewport rect.
 *
 * Seeks with a MutationObserver (App Router streams sections in after
 * navigation), then tracks the element with a rAF loop so the spotlight
 * stays glued through smooth scrolling, count-up animations, and async
 * sections above the anchor resizing as they settle. If the element never
 * appears within the timeout, resolves to "centered" so the tour never
 * hard-blocks. `resetKey` restarts the lifecycle (step or route change).
 *
 * While `enabled` is false (mid-navigation, before the step's route is
 * current) the hook holds in "seeking" without observers, so it can never
 * latch onto a same-named anchor on the outgoing page and the fallback
 * timeout doesn't start until the right page is up.
 */
export function useAnchorRect(
  anchor: string | undefined,
  resetKey: string,
  enabled = true
): AnchorState {
  const [state, setState] = useState<AnchorState>({
    phase: anchor ? "seeking" : "centered",
    rect: null,
  });

  // Reset synchronously during render when the step changes (React's
  // "adjust state while rendering" pattern), so the effect below never
  // needs a synchronous setState.
  const stepKey = `${anchor ?? ""}|${resetKey}`;
  const [lastStepKey, setLastStepKey] = useState(stepKey);
  if (lastStepKey !== stepKey) {
    setLastStepKey(stepKey);
    setState({ phase: anchor ? "seeking" : "centered", rect: null });
  }

  useEffect(() => {
    if (!anchor || !enabled) return;

    let disposed = false;
    let rafId = 0;
    let timeoutId = 0;
    let observer: MutationObserver | null = null;
    let lastRect: Rect | null = null;

    const selector = `[data-tour="${anchor}"]`;

    function startTracking(el: HTMLElement) {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      el.scrollIntoView({ block: "center", behavior: reduced ? "auto" : "smooth" });
      const loop = () => {
        if (disposed) return;
        const current = document.querySelector<HTMLElement>(selector);
        if (!current || !current.isConnected) {
          lastRect = null;
          seek();
          return;
        }
        const rect = toRect(current);
        if (rectChanged(lastRect, rect)) {
          lastRect = rect;
          setState({ phase: "anchored", rect });
        }
        rafId = requestAnimationFrame(loop);
      };
      rafId = requestAnimationFrame(loop);
    }

    function seek(initial = false) {
      // Initial state is already "seeking" (set during render); only a
      // re-seek after the element disconnects needs to announce it.
      if (!initial) setState({ phase: "seeking", rect: null });
      const el = document.querySelector<HTMLElement>(selector);
      if (el) {
        startTracking(el);
        return;
      }
      observer = new MutationObserver(() => {
        const found = document.querySelector<HTMLElement>(selector);
        if (found) {
          observer?.disconnect();
          observer = null;
          window.clearTimeout(timeoutId);
          startTracking(found);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      timeoutId = window.setTimeout(() => {
        if (disposed) return;
        observer?.disconnect();
        observer = null;
        setState({ phase: "centered", rect: null });
      }, SEEK_TIMEOUT_MS);
    }

    seek(true);

    return () => {
      disposed = true;
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      observer?.disconnect();
    };
  }, [anchor, resetKey, enabled]);

  return state;
}
